import React, { useState, useMemo } from 'react';

// Table columns with their types for appropriate filtering. Defined at module
// scope so its identity is stable across renders.
const columns = [
  { key: 'project_name', label: 'Project', type: 'text' },
  { key: 'unit_code', label: 'Unit No.', type: 'text' },
  { key: 'unit_type', label: 'Unit Type', type: 'text' },
  { key: 'total_price', label: 'Total Price (EGP)', type: 'number' },
  { key: 'down_payment', label: 'Down Payment (EGP)', type: 'number' },
  { key: 'installment_years', label: 'Installment (yrs)', type: 'number' },
  { key: 'area', label: 'Area (m²)', type: 'number' },
  { key: 'no_of_rooms', label: 'Rooms', type: 'number' },
  { key: 'finishing_type', label: 'Finishing', type: 'text' },
  { key: 'delivery_date', label: 'Delivery Date', type: 'text' },
  { key: 'payment_plan_id', label: 'Payment Plan', type: 'text' },
  { key: 'confidence_score', label: 'Confidence (%)', type: 'number' },
  { key: 'last_updated', label: 'Last Updated', type: 'date' }
];

const PropertyTable = ({ properties }) => {
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = properties.filter(property => {
      return columns.every(column => {
        const filterValue = filters[column.key];
        if (!filterValue) return true;

        const propertyValue = property[column.key];
        if (propertyValue === null || propertyValue === undefined) return false;

        switch (column.type) {
          case 'text':
            return propertyValue.toString().toLowerCase().includes(filterValue.toLowerCase());
          case 'number':
            const numValue = parseFloat(propertyValue);
            const filterNum = parseFloat(filterValue);
            return !isNaN(numValue) && !isNaN(filterNum) && numValue >= filterNum;
          case 'date':
            return new Date(propertyValue).toLocaleDateString().includes(filterValue);
          default:
            return true;
        }
      });
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        const direction = sortConfig.direction === 'asc' ? 1 : -1;
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * direction;
        }
        
        return aValue.toString().localeCompare(bValue.toString()) * direction;
      });
    }

    return filtered;
  }, [properties, filters, sortConfig]);

  const handleFilterChange = (columnKey, value) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  const handleSort = (columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSortConfig({ key: null, direction: 'asc' });
  };

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return 'N/A';

    switch (type) {
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      case 'date':
        return new Date(value).toLocaleDateString();
      default:
        return value;
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="property-table-container">
      <div className="table-controls">
        <div className="filter-summary">
          {Object.keys(filters).filter(key => filters[key]).length > 0 && (
            <span className="active-filters">
              {Object.keys(filters).filter(key => filters[key]).length} filter(s) active
            </span>
          )}
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear All Filters
          </button>
        </div>
        <div className="results-count">
          Showing {filteredAndSortedData.length} of {properties.length} properties
        </div>
      </div>

      <div className="table-wrapper">
        <table className="property-table">
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} className="sortable">
                  <div className="header-content">
                    <span 
                      className="header-label"
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label} {getSortIcon(column.key)}
                    </span>
                    <input
                      type={column.type === 'number' ? 'number' : 'text'}
                      placeholder={
                        column.type === 'number' ? 'Min value...' : 
                        column.type === 'date' ? 'Filter date...' : 
                        'Filter...'
                      }
                      value={filters[column.key] || ''}
                      onChange={(e) => handleFilterChange(column.key, e.target.value)}
                      className="filter-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((property, index) => (
              <tr key={index} className="property-row">
                {columns.map(column => (
                  <td key={column.key} className={`cell-${column.type}`}>
                    {column.key === 'confidence_score'
                      ? `${Math.round((property[column.key] || 0) * 100)}%`
                      : column.key === 'payment_plan_id' && property.payment_plan_details
                        ? (
                            <span title={
                              `Type: ${property.payment_plan_details.type}\n` +
                              `Total Price: ${property.payment_plan_details.total_price}\n` +
                              `Down Payment: ${property.payment_plan_details.down_payment}\n` +
                              `Installment Period: ${property.payment_plan_details.installment_period}\n` +
                              `Frequency: ${property.payment_plan_details.payment_frequency}\n` +
                              `Interest Rate: ${property.payment_plan_details.interest_rate}`
                            }>
                              {property.payment_plan_id}
                            </span>
                          )
                        : formatValue(property[column.key], column.type)
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedData.length === 0 && properties.length > 0 && (
        <div className="no-results">
          <p>🔍 No properties match your current filters.</p>
          <button className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default PropertyTable;
